import type { JSONContent } from '@tiptap/core';

export type MentionType =
  | 'asset'
  | 'avatar'
  | 'branding'
  | 'group'
  | 'product'
  | 'product_asset'
  | 'thread'
  | 'thread_asset'
  // Work Chats mention types. `user` addresses a workspace member
  // (`[@Maya](user:<userId>)`); `imai` addresses the general agent, pinned to
  // the top of the suggestion list (`[@imai](imai:imai)`).
  | 'user'
  | 'imai';

export interface MentionItem {
  id: string;
  label: string;
  type: MentionType;
  isEmptyState?: boolean;
  thumbnailUrl?: string;
  group?: string; // e.g. asset group like "lifestyle", "studio"
  sectionKey?: string;
  sectionLabel?: string;
  searchText?: string; // Additional text to search against (e.g. asset prompt)
  searchPriority?: number;
  mimeType?: string;
  storageKey?: string;
  storageId?: string;
  url?: string;
  referenceSource?:
    | 'thread'
    | 'product_preset'
    | 'style_preset'
    | 'color_preset'
    | 'my_reference'
    | 'other_reference';
  disabled?: boolean;
  disabledReason?: string;
  rawData?: Record<string, unknown>;
}

export interface FileChipData {
  uploadId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  status: string;
  previewUrl?: string;
  storageKey?: string;
  storageId?: string;
  url?: string;
  workspaceFileId?: string;
  vfsPath?: string;
  kind: 'image' | 'file';
  semanticType?:
    | 'reference'
    | 'color'
    | 'trend'
    | 'output'
    | 'design-intent'
    | 'connector'
    | 'plugin'
    | 'deep-research'
    | 'brand-intent'
    | 'skill'
    | 'canvas-annotation';
  displayMode?: 'image-only' | 'compact';
  referenceCategoryLabel?: string;
  colors?: Array<string>;
  productPresetCanonicalName?: string;
  productPresetSubtypeId?: string;
  productPresetSubtypeDescription?: string;
}

export function sanitizeMentionLabel(label: string): string {
  return label.replace(/[\]()]/g, '');
}

export function stripMentionSyntax(text: string): string {
  return text
    .replace(MENTION_RE, (_match, label: string) => label)
    .replace(FILE_CHIP_RE, (_match, filename: string) => `📎 ${filename}`);
}

/**
 * Serialize a TipTap document JSON to plain text with mention markers.
 *
 * Mention nodes become `[@Label](asset:<id>)` or `[@Label](avatar:<id>)`.
 * FileChip nodes are serialized as `[📎filename](file:<uploadId>)`.
 * Paragraph breaks become `\n`.
 */
export function serializeDocToMentionText(doc: {
  type: string;
  content?: Array<Record<string, unknown>>;
}): string {
  if (!doc.content) return '';

  const paragraphs: string[] = [];

  for (const block of doc.content) {
    if (block.type === 'paragraph') {
      const inner = (block.content as Array<Record<string, unknown>>) || [];
      let line = '';
      for (const node of inner) {
        if (node.type === 'text') {
          line += node.text as string;
        } else if (node.type === 'mention') {
          const attrs = node.attrs as {
            id: string;
            label: string;
            mentionType: MentionType;
          };
          line += `[@${sanitizeMentionLabel(attrs.label)}](${attrs.mentionType}:${attrs.id})`;
        } else if (node.type === 'fileChip') {
          const attrs = node.attrs as {
            filename: string;
            uploadId: string;
            vfsPath?: string | null;
            semanticType?: string | null;
          };
          const chipTarget =
            attrs.semanticType === 'skill' && attrs.vfsPath
              ? `user-skill:${attrs.vfsPath}`
              : attrs.uploadId;
          line += `[📎${attrs.filename}](file:${chipTarget})`;
        } else if (node.type === 'hardBreak') {
          line += '\n';
        }
      }
      paragraphs.push(line);
    }
  }

  return paragraphs.join('\n');
}

/**
 * Extract all fileChip nodes from a TipTap document JSON.
 */
export function extractFileChipsFromDoc(doc: {
  type: string;
  content?: Array<Record<string, unknown>>;
}): FileChipData[] {
  if (!doc.content) return [];

  const chips: FileChipData[] = [];

  for (const block of doc.content) {
    if (block.type === 'paragraph') {
      const inner = (block.content as Array<Record<string, unknown>>) || [];
      for (const node of inner) {
        if (node.type === 'fileChip') {
          const attrs = node.attrs as Record<string, unknown>;
          chips.push({
            uploadId: (attrs.uploadId as string) || '',
            filename: (attrs.filename as string) || '',
            mimeType: (attrs.mimeType as string) || '',
            sizeBytes: (attrs.sizeBytes as number) || 0,
            status: (attrs.status as string) || 'uploading',
            previewUrl: attrs.previewUrl as string | undefined,
            storageKey: attrs.storageKey as string | undefined,
            storageId: attrs.storageId as string | undefined,
            url: attrs.url as string | undefined,
            workspaceFileId: attrs.workspaceFileId as string | undefined,
            vfsPath: attrs.vfsPath as string | undefined,
            kind: (attrs.kind as 'image' | 'file') || 'image',
            semanticType:
              (attrs.semanticType as
                | 'reference'
                | 'color'
                | 'trend'
                | 'output'
                | 'design-intent'
                | 'connector'
                | 'plugin'
                | 'deep-research'
                | 'brand-intent'
                | 'skill'
                | 'canvas-annotation'
                | undefined) || undefined,
            displayMode:
              attrs.displayMode === 'image-only' ||
              attrs.displayMode === 'compact'
                ? attrs.displayMode
                : undefined,
            referenceCategoryLabel:
              typeof attrs.referenceCategoryLabel === 'string'
                ? attrs.referenceCategoryLabel
                : undefined,
            colors: Array.isArray(attrs.colors)
              ? attrs.colors.filter(
                  (color): color is string => typeof color === 'string'
                )
              : undefined,
            productPresetCanonicalName:
              typeof attrs.productPresetCanonicalName === 'string'
                ? attrs.productPresetCanonicalName
                : undefined,
            productPresetSubtypeId:
              typeof attrs.productPresetSubtypeId === 'string'
                ? attrs.productPresetSubtypeId
                : undefined,
            productPresetSubtypeDescription:
              typeof attrs.productPresetSubtypeDescription === 'string'
                ? attrs.productPresetSubtypeDescription
                : undefined,
          });
        }
      }
    }
  }

  return chips;
}

/**
 * Parse mention markers from serialized text.
 *
 * Returns the clean display text and an array of extracted mentions.
 *
 * NOTE: This is a client-side duplicate of the server-side implementation in
 * convex/products/ai/lib/parse_mentions.ts — keep them in sync.
 */
export interface ParsedMention {
  type: MentionType;
  id: string;
  label: string;
}

const MENTION_TYPES_PATTERN =
  'asset|avatar|branding|group|product|product_asset|thread|thread_asset|user|imai';
const MENTION_RE = new RegExp(
  String.raw`\[@([^\]]+)\]\((${MENTION_TYPES_PATTERN}):([^)]+)\)`,
  'g'
);
export const FILE_CHIP_RE = /\[📎([^\]]+)\]\(file:([^)]+)\)/g;

export function parseMentions(text: string): {
  cleanText: string;
  mentions: ParsedMention[];
} {
  const mentions: ParsedMention[] = [];
  const cleanText = text.replace(MENTION_RE, (_match, label, type, id) => {
    mentions.push({ type: type as MentionType, id, label });
    return label;
  });
  return { cleanText, mentions };
}

// ─── Channel (Work Chats) markdown serialization ───────────────────────────
//
// The org-chat serializer (`serializeDocToMentionText`) intentionally flattens
// TipTap StarterKit marks — it emits only plain text plus mention/file markers,
// so bold/italic/strike/inline-code typed in the composer are lost. Channel
// messages want that formatting to survive as markdown, so this serializer walks
// the same doc shape but emits markdown emphasis around marked text runs while
// preserving the identical `[@Label](type:id)` mention markers.
//
// This is additive and channel-only; org chat keeps calling
// `serializeDocToMentionText` unchanged.

interface SerializedTextNode {
  type?: string;
  text?: string;
  marks?: Array<{ type?: string }>;
  attrs?: Record<string, unknown>;
}

function markdownEscapeText(text: string): string {
  // Escape the markdown emphasis/code control characters we emit so literal
  // asterisks/backticks in user text don't get reinterpreted on render.
  return text.replace(/([\\`*_~])/g, '\\$1');
}

function wrapTextWithMarks(
  text: string,
  marks: Array<{ type?: string }>
): string {
  if (text.length === 0) return text;

  const markTypes = new Set(marks.map((mark) => mark?.type).filter(Boolean));
  // Inline code takes over the run: markdown does not nest emphasis inside code
  // spans, so emit a plain `code` span (without escaping, per markdown rules).
  if (markTypes.has('code')) {
    return `\`${text}\``;
  }

  let out = markdownEscapeText(text);
  // Innermost → outermost. Order is stable so round-tripping stays predictable.
  if (markTypes.has('italic')) out = `*${out}*`;
  if (markTypes.has('bold')) out = `**${out}**`;
  if (markTypes.has('strike')) out = `~~${out}~~`;
  return out;
}

function serializeInlineNode(node: SerializedTextNode): string {
  if (node.type === 'text') {
    return wrapTextWithMarks(
      (node.text as string) ?? '',
      Array.isArray(node.marks) ? node.marks : []
    );
  }
  if (node.type === 'mention') {
    const attrs = (node.attrs ?? {}) as {
      id: string;
      label: string;
      mentionType: MentionType;
    };
    return `[@${sanitizeMentionLabel(attrs.label)}](${attrs.mentionType}:${attrs.id})`;
  }
  if (node.type === 'fileChip') {
    const attrs = (node.attrs ?? {}) as {
      filename: string;
      uploadId: string;
      vfsPath?: string | null;
      semanticType?: string | null;
    };
    const chipTarget =
      attrs.semanticType === 'skill' && attrs.vfsPath
        ? `user-skill:${attrs.vfsPath}`
        : attrs.uploadId;
    return `[📎${attrs.filename}](file:${chipTarget})`;
  }
  if (node.type === 'hardBreak') {
    return '\n';
  }
  return '';
}

/**
 * Serialize a TipTap document JSON to markdown with mention markers, preserving
 * StarterKit inline marks (bold/italic/strike/inline-code). Channel-only —
 * do NOT use this for org chat, which relies on `serializeDocToMentionText`.
 *
 * Mentions serialize identically to `serializeDocToMentionText`
 * (`[@Label](type:id)`) so `parseMentions` / `parseMentionsFromBody` parse them.
 */
export function serializeDocToMarkdownWithMentions(doc: {
  type: string;
  content?: Array<Record<string, unknown>>;
}): string {
  if (!doc.content) return '';

  const lines: string[] = [];

  for (const block of doc.content) {
    if (block.type === 'paragraph') {
      const inner =
        (block.content as Array<SerializedTextNode>) ||
        ([] as SerializedTextNode[]);
      let line = '';
      for (const node of inner) {
        line += serializeInlineNode(node);
      }
      lines.push(line);
    }
  }

  // Trim only trailing empty lines so an intentional blank line between
  // paragraphs survives but a dangling empty paragraph doesn't add whitespace.
  while (lines.length > 0 && lines[lines.length - 1]?.trim() === '') {
    lines.pop();
  }

  return lines.join('\n');
}

/**
 * Parse mention markers out of a serialized channel message body.
 *
 * Returns the deduplicated set of mentions in the backend contract shape:
 * `{ kind: 'user' | 'agent'; userId?: string }`. `[@…](imai:imai)` maps to
 * `{ kind: 'agent' }`; `[@…](user:<id>)` maps to `{ kind: 'user', userId }`.
 * Other mention types (assets, threads, …) are ignored — they are not valid
 * channel-message recipients.
 */
export function parseMentionsFromBody(
  body: string
): Array<{ kind: 'user' | 'agent'; userId?: string }> {
  const { mentions } = parseMentions(body);
  const result: Array<{ kind: 'user' | 'agent'; userId?: string }> = [];
  const seenUserIds = new Set<string>();
  let hasAgent = false;

  for (const mention of mentions) {
    if (mention.type === 'imai') {
      if (!hasAgent) {
        hasAgent = true;
        result.push({ kind: 'agent' });
      }
      continue;
    }
    if (mention.type === 'user') {
      if (mention.id && !seenUserIds.has(mention.id)) {
        seenUserIds.add(mention.id);
        result.push({ kind: 'user', userId: mention.id });
      }
    }
  }

  return result;
}

export function deserializeMentionTextToDoc(text: string): JSONContent {
  const paragraphs = text.split('\n').map((line) => {
    const content: JSONContent[] = [];
    const pattern = new RegExp(
      String.raw`\[@([^\]]+)\]\((${MENTION_TYPES_PATTERN}):([^)]+)\)|\[📎([^\]]+)\]\(file:([^)]+)\)`,
      'g'
    );
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(line)) !== null) {
      if (match.index > lastIndex) {
        content.push({
          type: 'text',
          text: line.slice(lastIndex, match.index),
        });
      }

      if (match[1] && match[2] && match[3]) {
        content.push({
          type: 'mention',
          attrs: {
            id: match[3],
            label: match[1],
            mentionType: match[2] as MentionType,
          },
        });
      } else if (match[4] && match[5]) {
        const uploadId = match[5];
        const userSkillPath = uploadId.startsWith('user-skill:')
          ? uploadId.slice('user-skill:'.length)
          : null;
        content.push({
          type: 'fileChip',
          attrs: {
            uploadId,
            filename: match[4],
            mimeType: '',
            sizeBytes: 0,
            status: 'complete',
            kind: 'file',
            ...(userSkillPath
              ? { semanticType: 'skill', vfsPath: userSkillPath }
              : {}),
          },
        });
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < line.length) {
      content.push({
        type: 'text',
        text: line.slice(lastIndex),
      });
    }

    return content.length > 0
      ? ({ type: 'paragraph', content } satisfies JSONContent)
      : ({ type: 'paragraph' } satisfies JSONContent);
  });

  return {
    type: 'doc',
    content: paragraphs.length > 0 ? paragraphs : [{ type: 'paragraph' }],
  };
}
